// src/pages/cadastros/UsuariosPage.tsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Table, Button, Form, InputGroup, Row, Col,
  Modal, Spinner, Alert, Pagination
} from 'react-bootstrap';
import {
  FaSearch, FaPlus, FaEdit, FaTrash, FaExclamationTriangle, FaSave, FaEye, FaEyeSlash
} from 'react-icons/fa';
import useSortable from '../../hooks/useSortable';
import SortableTh from '../../components/SortableTh';
import { Formik, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import api from '../../service/api';

interface Usuario {
  ID: number;
  NOME: string;
  EMAIL: string;
}

interface UsuarioCreateValues {
  NOME: string;
  EMAIL: string;
  SENHA: string;
  CONFIRMACAO_SENHA: string;
}

interface UsuarioEditValues {
  NOME: string;
  EMAIL: string;
  SENHA: string;
}

const ITEMS_PER_PAGE = 10;

const createSchema = Yup.object({
  NOME: Yup.string().required('Nome é obrigatório').max(100),
  EMAIL: Yup.string().email('Email inválido').required('Email é obrigatório').max(100),
  SENHA: Yup.string().required('Senha é obrigatória').min(6, 'Mínimo 6 caracteres'),
  CONFIRMACAO_SENHA: Yup.string()
    .required('Confirmação é obrigatória')
    .oneOf([Yup.ref('SENHA')], 'Senhas não conferem'),
});

const editSchema = Yup.object({
  NOME: Yup.string().required('Nome é obrigatório').max(100),
  EMAIL: Yup.string().email('Email inválido').required('Email é obrigatório').max(100),
  SENHA: Yup.string().min(6, 'Mínimo 6 caracteres'),
});

const UsuariosPage: React.FC = () => {
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [currentUsuario, setCurrentUsuario] = useState<Usuario | null>(null);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [usuarioToDelete, setUsuarioToDelete] = useState<Usuario | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const fetchUsuarios = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.get('/usuarios');
      setUsuarios(res.data);
      setCurrentPage(1);
    } catch {
      setError('Não foi possível carregar os usuários.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchUsuarios(); }, [fetchUsuarios]);

  const filtered = usuarios.filter(u =>
    u.NOME?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.EMAIL?.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const { sortedData: sortedUsuarios, sortKey, sortDir, handleSort } = useSortable<Usuario>(filtered, 'NOME');
  const paginated = sortedUsuarios.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);

  const handleCreate = async (
    values: UsuarioCreateValues,
    { resetForm }: FormikHelpers<UsuarioCreateValues>
  ) => {
    try {
      setFormSubmitting(true);
      await api.post('/auth/registrar', { nome: values.NOME, email: values.EMAIL, senha: values.SENHA });
      setShowModal(false);
      resetForm();
      fetchUsuarios();
    } catch {
      setError('Não foi possível criar o usuário. Verifique se o email já está em uso.');
    } finally {
      setFormSubmitting(false);
    }
  };

  const handleEdit = async (
    values: UsuarioEditValues,
    { resetForm }: FormikHelpers<UsuarioEditValues>
  ) => {
    try {
      setFormSubmitting(true);
      const payload: any = { NOME: values.NOME, EMAIL: values.EMAIL };
      if (values.SENHA) payload.SENHA = values.SENHA;
      await api.put(`/usuarios/${currentUsuario!.ID}`, payload);
      setShowModal(false);
      resetForm();
      fetchUsuarios();
    } catch {
      setError('Não foi possível salvar. Tente novamente.');
    } finally {
      setFormSubmitting(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!usuarioToDelete) return;
    try {
      setDeleteLoading(true);
      await api.delete(`/usuarios/${usuarioToDelete.ID}`);
      setShowDeleteModal(false);
      setUsuarioToDelete(null);
      fetchUsuarios();
    } catch {
      setError('Não foi possível excluir. Tente novamente.');
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1>Usuários do Sistema</h1>
        <Button variant="primary" onClick={() => { setModalMode('create'); setCurrentUsuario(null); setShowModal(true); }}>
          {FaPlus({ className: "me-2" })} Novo Usuário
        </Button>
      </div>

      {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{FaExclamationTriangle({ className: "me-2" })}{error}</Alert>}

      <Card className="shadow-sm mb-4">
        <Card.Body>
          <Row className="mb-3">
            <Col md={6}>
              <InputGroup>
                <InputGroup.Text>{FaSearch({})}</InputGroup.Text>
                <Form.Control
                  placeholder="Buscar por nome ou email..."
                  value={searchTerm}
                  onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                />
              </InputGroup>
            </Col>
            <Col md={6} className="d-flex justify-content-md-end align-items-center">
              <span className="text-muted">Total: {filtered.length}</span>
            </Col>
          </Row>

          {loading ? (
            <div className="text-center py-5"><Spinner animation="border" variant="primary" /><p className="mt-3">Carregando...</p></div>
          ) : filtered.length === 0 ? (
            <Alert variant="info">Nenhum usuário encontrado.</Alert>
          ) : (
            <>
              <Table hover>
                <thead className="table-light">
                  <tr>
                    <SortableTh<Usuario> label="ID" sortKey="ID" currentKey={sortKey} dir={sortDir} onSort={handleSort} />
                    <SortableTh<Usuario> label="Nome" sortKey="NOME" currentKey={sortKey} dir={sortDir} onSort={handleSort} />
                    <SortableTh<Usuario> label="Email" sortKey="EMAIL" currentKey={sortKey} dir={sortDir} onSort={handleSort} />
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {paginated.map(u => (
                    <tr key={u.ID}>
                      <td>{u.ID}</td>
                      <td>{u.NOME}</td>
                      <td>{u.EMAIL}</td>
                      <td>
                        <Button variant="outline-primary" size="sm" className="me-2" onClick={() => { setModalMode('edit'); setCurrentUsuario(u); setShowModal(true); }} title="Editar">{FaEdit({})}</Button>
                        <Button variant="outline-danger" size="sm" onClick={() => { setUsuarioToDelete(u); setShowDeleteModal(true); }} title="Excluir">{FaTrash({})}</Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              {totalPages > 1 && (
                <div className="d-flex justify-content-center mt-4">
                  <Pagination>
                    <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
                    <Pagination.Prev onClick={() => setCurrentPage(p => Math.max(p - 1, 1))} disabled={currentPage === 1} />
                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                      .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 2)
                      .map((p, idx, arr) => (
                        <React.Fragment key={p}>
                          {arr[idx - 1] && p - arr[idx - 1] > 1 && <Pagination.Ellipsis disabled />}
                          <Pagination.Item active={p === currentPage} onClick={() => setCurrentPage(p)}>{p}</Pagination.Item>
                        </React.Fragment>
                      ))}
                    <Pagination.Next onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))} disabled={currentPage === totalPages} />
                    <Pagination.Last onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} />
                  </Pagination>
                </div>
              )}
            </>
          )}
        </Card.Body>
      </Card>

      {/* Modal Criar */}
      {modalMode === 'create' && (
        <Modal show={showModal} onHide={() => setShowModal(false)} centered>
          <Modal.Header closeButton><Modal.Title>Novo Usuário</Modal.Title></Modal.Header>
          <Formik
            initialValues={{ NOME: '', EMAIL: '', SENHA: '', CONFIRMACAO_SENHA: '' }}
            validationSchema={createSchema}
            onSubmit={handleCreate}
          >
            {({ values, errors, touched, handleChange, handleBlur, handleSubmit: fs }) => (
              <Form onSubmit={fs}>
                <Modal.Body>
                  <Form.Group className="mb-3">
                    <Form.Label>Nome *</Form.Label>
                    <Form.Control name="NOME" value={values.NOME} onChange={handleChange} onBlur={handleBlur} isInvalid={touched.NOME && !!errors.NOME} />
                    <Form.Control.Feedback type="invalid">{errors.NOME}</Form.Control.Feedback>
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Email *</Form.Label>
                    <Form.Control type="email" name="EMAIL" value={values.EMAIL} onChange={handleChange} onBlur={handleBlur} isInvalid={touched.EMAIL && !!errors.EMAIL} />
                    <Form.Control.Feedback type="invalid">{errors.EMAIL}</Form.Control.Feedback>
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Senha *</Form.Label>
                    <InputGroup>
                      <Form.Control type={showPassword ? 'text' : 'password'} name="SENHA" value={values.SENHA} onChange={handleChange} onBlur={handleBlur} isInvalid={touched.SENHA && !!errors.SENHA} />
                      <Button variant="outline-secondary" onClick={() => setShowPassword(p => !p)}>{showPassword ? FaEyeSlash({}) : FaEye({})}</Button>
                      <Form.Control.Feedback type="invalid">{errors.SENHA}</Form.Control.Feedback>
                    </InputGroup>
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Confirmar Senha *</Form.Label>
                    <Form.Control type={showPassword ? 'text' : 'password'} name="CONFIRMACAO_SENHA" value={values.CONFIRMACAO_SENHA} onChange={handleChange} onBlur={handleBlur} isInvalid={touched.CONFIRMACAO_SENHA && !!errors.CONFIRMACAO_SENHA} />
                    <Form.Control.Feedback type="invalid">{errors.CONFIRMACAO_SENHA}</Form.Control.Feedback>
                  </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                  <Button variant="secondary" onClick={() => setShowModal(false)}>Cancelar</Button>
                  <Button variant="primary" type="submit" disabled={formSubmitting}>{formSubmitting ? 'Salvando...' : <>{FaSave({ className: "me-2" })}Salvar</>}</Button>
                </Modal.Footer>
              </Form>
            )}
          </Formik>
        </Modal>
      )}

      {/* Modal Editar */}
      {modalMode === 'edit' && (
        <Modal show={showModal} onHide={() => setShowModal(false)} centered>
          <Modal.Header closeButton><Modal.Title>Editar Usuário</Modal.Title></Modal.Header>
          <Formik
            initialValues={{ NOME: currentUsuario?.NOME ?? '', EMAIL: currentUsuario?.EMAIL ?? '', SENHA: '' }}
            validationSchema={editSchema}
            onSubmit={handleEdit}
            enableReinitialize
          >
            {({ values, errors, touched, handleChange, handleBlur, handleSubmit: fs }) => (
              <Form onSubmit={fs}>
                <Modal.Body>
                  <Form.Group className="mb-3">
                    <Form.Label>Nome *</Form.Label>
                    <Form.Control name="NOME" value={values.NOME} onChange={handleChange} onBlur={handleBlur} isInvalid={touched.NOME && !!errors.NOME} />
                    <Form.Control.Feedback type="invalid">{errors.NOME}</Form.Control.Feedback>
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Email *</Form.Label>
                    <Form.Control type="email" name="EMAIL" value={values.EMAIL} onChange={handleChange} onBlur={handleBlur} isInvalid={touched.EMAIL && !!errors.EMAIL} />
                    <Form.Control.Feedback type="invalid">{errors.EMAIL}</Form.Control.Feedback>
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Nova Senha <small className="text-muted">(deixe em branco para não alterar)</small></Form.Label>
                    <InputGroup>
                      <Form.Control type={showPassword ? 'text' : 'password'} name="SENHA" value={values.SENHA} onChange={handleChange} onBlur={handleBlur} isInvalid={touched.SENHA && !!errors.SENHA} />
                      <Button variant="outline-secondary" onClick={() => setShowPassword(p => !p)}>{showPassword ? FaEyeSlash({}) : FaEye({})}</Button>
                      <Form.Control.Feedback type="invalid">{errors.SENHA}</Form.Control.Feedback>
                    </InputGroup>
                  </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                  <Button variant="secondary" onClick={() => setShowModal(false)}>Cancelar</Button>
                  <Button variant="primary" type="submit" disabled={formSubmitting}>{formSubmitting ? 'Salvando...' : <>{FaSave({ className: "me-2" })}Salvar</>}</Button>
                </Modal.Footer>
              </Form>
            )}
          </Formik>
        </Modal>
      )}

      {/* Modal Excluir */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton><Modal.Title>Confirmar Exclusão</Modal.Title></Modal.Header>
        <Modal.Body className="text-center py-4">
          {FaExclamationTriangle({ size: 48, className: "text-danger mb-3" })}
          <p>Excluir o usuário <strong>{usuarioToDelete?.NOME}</strong>?</p>
          <p className="text-danger small">Esta ação não pode ser desfeita.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>Cancelar</Button>
          <Button variant="danger" onClick={handleConfirmDelete} disabled={deleteLoading}>{deleteLoading ? 'Excluindo...' : 'Excluir'}</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default UsuariosPage;
